<script setup lang="ts">
import CurrencyText from '@uiKit/components/CurrencyText.vue'
import { nextTick, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import AskSliceyMessageBubble from './AskSliceyMessageBubble.vue'
import type { SliceyMessage } from '../types/messageTypes'

interface Props {
  messages: SliceyMessage[]
  isThinking: boolean
  starterPrompts: string[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'pick', prompt: string): void
}>()

const scrollEl = ref<HTMLElement | null>(null)

const scrollToBottom = () => {
  const el = scrollEl.value
  if (!el) return
  el.scrollTop = el.scrollHeight
}

watch(
  () => [props.messages.length, props.isThinking],
  () => {
    nextTick(scrollToBottom)
  },
)
</script>

<template>
  <Box ref="scrollEl" direction="col" gap="3" class="conversation">
    <template v-if="messages.length === 0">
      <Box direction="col" gap="2" align="start" class="conversation__intro">
        <Text type="heading" weight="semibold">Hi, I'm Slicey.</Text>
        <Text type="body-sm" color="muted">
          Ask me anything about your portfolio, properties on offer, or the markets. I'll keep it
          short and pull from your account where it helps.
        </Text>
      </Box>

      <Box direction="col" gap="2" class="conversation__starters">
        <Text type="micro" color="muted">TRY ASKING</Text>
        <Box
          v-for="prompt in starterPrompts"
          :key="prompt"
          is="button"
          align="center"
          gap="2"
          class="conversation__starter"
          @click="emit('pick', prompt)"
        >
          <PiIcon name="comments" class="conversation__starter-icon" />
          <Text type="body-sm" weight="medium"><CurrencyText :text="prompt" /></Text>
        </Box>
      </Box>
    </template>

    <template v-else>
      <AskSliceyMessageBubble
        v-for="message in messages"
        :key="message.id"
        :author="message.author"
        :text="message.text"
      />

      <Box v-if="isThinking" align="center" gap="2" class="conversation__thinking">
        <Box class="conversation__dot conversation__dot--1" />
        <Box class="conversation__dot conversation__dot--2" />
        <Box class="conversation__dot conversation__dot--3" />
        <Text type="caption" color="muted">Slicey is thinking</Text>
      </Box>
    </template>
  </Box>
</template>

<style scoped>
.conversation {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.25rem 0.5rem;
  min-height: 0;
}

.conversation__intro {
  padding: 0.25rem 0 0.5rem;
}

.conversation__intro-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--p-primary-50);
  color: var(--p-primary-600);
  font-size: 0.95rem;
}

.conversation__starters {
  margin-top: 0.5rem;
}

.conversation__starter {
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: 12px;
  padding: 0.625rem 0.875rem;
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;
}

.conversation__starter:hover {
  border-color: var(--p-primary-200);
  background: var(--p-primary-50);
}

.conversation__starter-icon {
  color: var(--p-primary-600);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.conversation__thinking {
  align-self: flex-start;
  padding: 0.5rem 0.875rem;
  background: var(--p-surface-100);
  border: 1px solid var(--p-content-border-color);
  border-radius: 14px;
  border-bottom-left-radius: 4px;
}

.conversation__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--p-text-muted-color);
  animation: slicey-typing 1.2s infinite ease-in-out;
}

.conversation__dot--2 {
  animation-delay: 0.15s;
}

.conversation__dot--3 {
  animation-delay: 0.3s;
}

@keyframes slicey-typing {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-2px);
  }
}
</style>
